import React from 'react';
import { Check } from 'lucide-react';
import * as Icons from 'lucide-react';
import { services } from '../utils/mock';

const Services = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Our <span className="text-cyan-400">Services</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Comprehensive technology training and services tailored for students and educational institutions
          </p>
        </div>
      </section>

      {/* Services Details */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-24">
          {services.map((service, index) => {
            const IconComponent = Icons[service.icon.split('-').map(word => 
              word.charAt(0).toUpperCase() + word.slice(1)
            ).join('')] || Icons.Box;
            
            return (
              <div
                key={service.id}
                className={`flex flex-col ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                } items-center gap-12`}
              >
                {/* Icon/Visual Side */}
                <div className="flex-1 flex items-center justify-center">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-3xl blur-2xl"></div>
                    <div className="relative w-64 h-64 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950/50 backdrop-blur-sm border border-cyan-500/30 rounded-3xl flex items-center justify-center">
                      <IconComponent className="text-cyan-400" size={120} strokeWidth={1.5} />
                    </div>
                  </div>
                </div>

                {/* Content Side */}
                <div className="flex-1 space-y-6">
                  <div className="inline-block px-4 py-1.5 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm font-medium">
                    Service {service.id}
                  </div>
                  <h2 className="text-4xl font-bold text-white">{service.title}</h2>
                  <p className="text-lg text-gray-400 leading-relaxed">
                    {service.description}
                  </p>
                  <div className="space-y-3 pt-4">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center space-x-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-cyan-500/20 rounded-full flex items-center justify-center">
                          <Check className="text-cyan-400" size={14} />
                        </div>
                        <span className="text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Detailed Service Sections */}
      <section className="py-24 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* AI, Robotics & IoT Workshops */}
            <div className="bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950/50 backdrop-blur-sm border border-cyan-500/20 rounded-2xl p-8 hover:border-cyan-500/50 transition-all duration-300">
              <h3 className="text-2xl font-bold text-white mb-4">AI, Robotics & IoT Workshops</h3>
              <p className="text-gray-400 mb-6">
                Comprehensive hands-on training programs designed for school and college students to master emerging technologies.
              </p>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start space-x-3">
                  <Check className="text-cyan-400 flex-shrink-0 mt-1" size={18} />
                  <span>Introduction to Artificial Intelligence and Machine Learning</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="text-cyan-400 flex-shrink-0 mt-1" size={18} />
                  <span>Robotics fundamentals and automation projects</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="text-cyan-400 flex-shrink-0 mt-1" size={18} />
                  <span>IoT sensor integration and smart device creation</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="text-cyan-400 flex-shrink-0 mt-1" size={18} />
                  <span>Real-world project implementation and certification</span>
                </li>
              </ul>
            </div>

            {/* 3D Printing Services */}
            <div className="bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950/50 backdrop-blur-sm border border-cyan-500/20 rounded-2xl p-8 hover:border-cyan-500/50 transition-all duration-300">
              <h3 className="text-2xl font-bold text-white mb-4">3D Printing Services</h3>
              <p className="text-gray-400 mb-6">
                Professional rapid prototyping and model development services for educational projects and innovations.
              </p>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start space-x-3">
                  <Check className="text-cyan-400 flex-shrink-0 mt-1" size={18} />
                  <span>High-quality 3D printing for prototypes and models</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="text-cyan-400 flex-shrink-0 mt-1" size={18} />
                  <span>Design optimization and printing consultation</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="text-cyan-400 flex-shrink-0 mt-1" size={18} />
                  <span>Multiple material options and finishing services</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="text-cyan-400 flex-shrink-0 mt-1" size={18} />
                  <span>Special educational discounts and bulk pricing</span>
                </li>
              </ul>
            </div>

            {/* PCB Designing */}
            <div className="bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950/50 backdrop-blur-sm border border-cyan-500/20 rounded-2xl p-8 hover:border-cyan-500/50 transition-all duration-300">
              <h3 className="text-2xl font-bold text-white mb-4">PCB Designing Services</h3>
              <p className="text-gray-400 mb-6">
                Custom printed circuit board design and development for academic projects and innovative solutions.
              </p>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start space-x-3">
                  <Check className="text-cyan-400 flex-shrink-0 mt-1" size={18} />
                  <span>Custom PCB design from concept to production</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="text-cyan-400 flex-shrink-0 mt-1" size={18} />
                  <span>Circuit simulation and testing support</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="text-cyan-400 flex-shrink-0 mt-1" size={18} />
                  <span>Complete documentation and design files</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="text-cyan-400 flex-shrink-0 mt-1" size={18} />
                  <span>Fast turnaround time for urgent projects</span>
                </li>
              </ul>
            </div>

            {/* College Project Guidance */}
            <div className="bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950/50 backdrop-blur-sm border border-cyan-500/20 rounded-2xl p-8 hover:border-cyan-500/50 transition-all duration-300">
              <h3 className="text-2xl font-bold text-white mb-4">College Project Guidance</h3>
              <p className="text-gray-400 mb-6">
                End-to-end support for engineering students from project ideation to final presentation and documentation.
              </p>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start space-x-3">
                  <Check className="text-cyan-400 flex-shrink-0 mt-1" size={18} />
                  <span>Project topic selection and feasibility analysis</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="text-cyan-400 flex-shrink-0 mt-1" size={18} />
                  <span>Technical guidance and implementation support</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="text-cyan-400 flex-shrink-0 mt-1" size={18} />
                  <span>Complete documentation and report preparation</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="text-cyan-400 flex-shrink-0 mt-1" size={18} />
                  <span>Presentation skills training and review sessions</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 backdrop-blur-sm border border-cyan-500/30 rounded-2xl p-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Interested in Our Services?
            </h2>
            <p className="text-lg text-gray-400 mb-8">
              Let's discuss how we can help your institution or students achieve their technology goals
            </p>
            <a
              href="/contact"
              className="inline-block px-8 py-4 bg-cyan-500 text-white rounded-lg font-semibold hover:bg-cyan-600 transform hover:scale-105 transition-all duration-300 shadow-lg shadow-cyan-500/30"
            >
              Contact Us Today
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
